/**
 * Serviço para gerenciar notificações nativas do navegador
 * Garante que notificações sejam exibidas quando o app estiver minimizado ou em background
 */
class NotificacaoNativaService {
  private ultimasNotificacoes: Set<string> = new Set();
  private permissaoSolicitada: boolean = false;

  /**
   * Solicita permissão para notificações nativas
   */
  async solicitarPermissao(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Este navegador não suporta notificações nativas');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Usuário negou permissão para notificações');
      return false;
    }

    if (!this.permissaoSolicitada) {
      this.permissaoSolicitada = true;
      try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
      } catch (error) {
        console.error('Erro ao solicitar permissão para notificações:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Verifica se a página está visível (não minimizada/em background)
   */
  private isPaginaVisivel(): boolean {
    return document.visibilityState === 'visible' && !document.hidden;
  }

  /**
   * Cria uma chave única para evitar notificações duplicadas
   */
  private criarChaveNotificacao(titulo: string, mensagem: string): string {
    return `${titulo}-${mensagem}`.replace(/\s+/g, '-').toLowerCase();
  }

  /**
   * Envia notificação nativa do navegador
   * @param titulo Título da notificação
   * @param mensagem Corpo da notificação
   * @param opcoes Opções adicionais
   * @param forcarExibicao Se true, exibe mesmo com página visível
   */
  async enviarNotificacao(
    titulo: string, 
    mensagem: string, 
    opcoes: {
      icon?: string;
      tag?: string;
      requireInteraction?: boolean;
      silent?: boolean;
      forcarExibicao?: boolean;
    } = {}
  ): Promise<boolean> {
    // Verificar se notificações são suportadas
    if (!('Notification' in window)) {
      console.warn('Notificações nativas não suportadas');
      return false;
    }

    // Solicitar permissão se necessário
    const temPermissao = await this.solicitarPermissao();
    if (!temPermissao) {
      console.warn('Sem permissão para notificações nativas');
      return false;
    }

    // Verificar se deve exibir notificação
    const paginaVisivel = this.isPaginaVisivel();
    if (paginaVisivel && !opcoes.forcarExibicao) {
      console.log('Página visível, não enviando notificação nativa');
      return false;
    }

    // Evitar notificações duplicadas
    const chave = this.criarChaveNotificacao(titulo, mensagem);
    if (this.ultimasNotificacoes.has(chave)) {
      console.log('Notificação duplicada ignorada:', chave);
      return false;
    }

    try {
      // Configurar opções da notificação
      const notificationOptions: NotificationOptions = {
        body: mensagem,
        icon: opcoes.icon || '/favicon.ico',
        tag: opcoes.tag || chave,
        requireInteraction: opcoes.requireInteraction || true,
        silent: opcoes.silent || false,
        badge: '/favicon.ico',
        ...opcoes
      };

      // Criar notificação nativa
      const notification = new Notification(titulo, notificationOptions);

      // Adicionar à lista de notificações recentes
      this.ultimasNotificacoes.add(chave);

      // Limpar da lista após 5 minutos para permitir nova notificação
      setTimeout(() => {
        this.ultimasNotificacoes.delete(chave);
      }, 5 * 60 * 1000);

      // Configurar eventos da notificação
      notification.onclick = () => {
        console.log('Notificação clicada, focando na janela');
        window.focus();
        notification.close();
      };

      notification.onclose = () => {
        console.log('Notificação fechada');
      };

      notification.onerror = (error) => {
        console.error('Erro na notificação:', error);
      };

      // Fechar automaticamente após 10 segundos
      setTimeout(() => {
        notification.close();
      }, 10000);

      console.log('Notificação nativa enviada:', titulo);
      return true;

    } catch (error) {
      console.error('Erro ao criar notificação nativa:', error);
      return false;
    }
  }

  /**
   * Envia notificação específica para nova tarefa
   */
  async notificarNovaTarefa(tituloTarefa: string, criador?: string): Promise<boolean> {
    return this.enviarNotificacao(
      '📋 Nova Tarefa Recebida!',
      `${tituloTarefa}${criador ? ` (de ${criador})` : ''}`,
      {
        tag: 'nova-tarefa',
        requireInteraction: true,
        icon: '/favicon.ico'
      }
    );
  }

  /**
   * Envia notificação para mudança de status
   */
  async notificarMudancaStatus(tituloTarefa: string, novoStatus: string): Promise<boolean> {
    return this.enviarNotificacao(
      '🔄 Status de Tarefa Alterado',
      `${tituloTarefa} → ${novoStatus}`,
      {
        tag: 'mudanca-status',
        requireInteraction: false,
        icon: '/favicon.ico'
      }
    );
  }

  /**
   * Envia notificação para tarefas pendentes (múltiplas)
   */
  async notificarTarefasPendentes(quantidade: number): Promise<boolean> {
    if (quantidade === 0) return false;

    const titulo = quantidade === 1 ? 
      '⚠️ Você tem 1 tarefa pendente!' : 
      `⚠️ Você tem ${quantidade} tarefas pendentes!`;

    const mensagem = quantidade === 1 ? 
      'Clique para visualizar sua tarefa pendente' : 
      'Clique para visualizar suas tarefas pendentes';

    return this.enviarNotificacao(
      titulo,
      mensagem,
      {
        tag: 'tarefas-pendentes',
        requireInteraction: true,
        icon: '/favicon.ico'
      }
    );
  }

  /**
   * Limpa todas as notificações pendentes
   */
  limparNotificacoes(): void {
    this.ultimasNotificacoes.clear();
    console.log('Cache de notificações limpo');
  }

  /**
   * Verifica o status da permissão
   */
  getStatusPermissao(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }
}

// Exportar instância única
export default new NotificacaoNativaService();
